import { useLocation } from "wouter";

import { ReminderToggleRow } from "../components/ReminderToggleRow";
import { SignOutButton } from "../components/SignOutButton";
import type { UseReminderPrefsResult } from "../hooks/useReminderPrefs";
import { SIGN_IN_SETTINGS_ROW_SUB, SIGN_IN_SETTINGS_ROW_TITLE } from "../lib/authCopy";
import { PROJECTS_ROW_SUB_ADMIN, PROJECTS_ROW_SUB_MEMBER } from "../lib/projectTasksCopy";

// Sprint-008 A1 — "Animals" and "Projects" were local `subView` states
// here; they are now routed (`/animals`, `/projects` — see
// `AnimalsScreen.tsx`/`ProjectsScreen.tsx`, mounted by `app.tsx`) per the
// task brief's explicit route list. "Sign in & security" stayed a local
// subview at the time — it wasn't named in that list, and — like Admin's
// People/Chores sub-tabs — was left deliberately state-driven, "revisit
// if A2's sidebar promotes it to a first-class entry."
//
// Sprint-016 F3 (MORE-01, ux-audit-whole-app-2026-07-11.md — S3) — that
// revisit: `/settings` (`SettingsView.tsx`) already existed (A2 built it
// for the desktop sidebar) and already rendered the identical
// `SignInSettingsView` content with the app's governed route-change focus
// convention — the phone entry point was simply never repointed at it.
// The row below now calls `navigate("/settings")`, the same
// `useLocation()` shape every sibling row already uses; the local
// `subView`/`MoreSubView` machinery this docstring used to describe is
// gone.

export interface MoreViewProps {
  /** admin/manager only — gates the Projects nav row (and, transitively,
   * whether `useProjects` ever fetches — see that hook's own docstring).
   * A plain member never sees "Projects" in this menu, matching
   * `GET /projects`'s 403 for that role (ADR-002 §5/§11). */
  canHandOutWork: boolean;
  /** Sprint-017 E7 (ADR-003 §3.3) — distinguishes the Projects row
   * sub-label: admin → "What's underway on the property."; member/manager →
   * "Your lists, and anything you're helping with." Defaults to `false` so
   * the pre-existing MoreView tests that don't pass it read the
   * member/manager copy unchanged. */
  isAdmin?: boolean;
  /** Sprint-005 C6 — the single `useReminderPrefs()` instance shared with
   * the Today opt-in card (see `app.tsx`'s docstring on that hook call);
   * drives the "Daily reminders" self-serve row below. */
  reminderPrefs: UseReminderPrefsResult;
  /** UX review 2026-10 (fix/ux-signin) — `useMe().logout`, threaded from
   * `app.tsx`. Renders the confirm-first "Sign out" action at the bottom
   * of this menu (the app-bar's instant "Done for now" is gone). Optional
   * only so isolated renders of this menu needn't pass it; `app.tsx`
   * always does. */
  onSignOut?: () => void | Promise<void>;
}

/**
 * Sprint-005 C4 — the "More" tab: a small landing menu (Animals, and — for
 * admin/manager — Projects) rather than crowding the tab bar with two more
 * top-level tabs. Visible to every authed role (member/manager/admin
 * alike get "More"; only its Projects row is role-gated away for a
 * member). See the sprint-005 C4 report for the tab-bar-growth rationale
 * flagged for design QA.
 */
export function MoreView({
  canHandOutWork,
  isAdmin = false,
  reminderPrefs,
  onSignOut,
}: MoreViewProps) {
  const [, navigate] = useLocation();

  return (
    <section class="view more-view" aria-label="More">
      <h1 class="view-heading">More</h1>
      <p class="view-subheading">Animals, projects, and your reminders.</p>

      <div class="admin-list">
        {/* Sprint-009 B4 (design-phase-b.md §1.2) — everyone-visible, same
         * standalone-screen shape as Animals below it, so it gets the
         * same landing-row treatment, placed above it. */}
        <button type="button" class="admin-row" onClick={() => navigate("/calendar")}>
          <span class="admin-row-main">
            <span class="admin-row-name">Calendar</span>
            <span class="admin-row-sub">What's coming up, and what happened.</span>
          </span>
          <span class="chevron" aria-hidden="true">
            ›
          </span>
        </button>

        <button type="button" class="admin-row" onClick={() => navigate("/animals")}>
          <span class="admin-row-main">
            <span class="admin-row-name">Animals</span>
            <span class="admin-row-sub">Who's on the farm, and their care history.</span>
          </span>
          <span class="chevron" aria-hidden="true">
            ›
          </span>
        </button>

        {/* Sprint-017 E7 (design §2.1, ADR-003 §3.3) — the Projects entry is
         * open to EVERY authenticated role now (the scope of the list behind
         * it is server-decided, never a client role check). The sub-label is
         * role-dependent copy. */}
        <button type="button" class="admin-row" onClick={() => navigate("/projects")}>
          <span class="admin-row-main">
            <span class="admin-row-name">Projects</span>
            <span class="admin-row-sub">
              {isAdmin ? PROJECTS_ROW_SUB_ADMIN : PROJECTS_ROW_SUB_MEMBER}
            </span>
          </span>
          <span class="chevron" aria-hidden="true">
            ›
          </span>
        </button>

        {/* Sprint-010 C4 (design-phase-c.md §1.2) — same gate/position rule
         * as Projects above it: standalone, not admin-console-only (a
         * manager without `isAdmin` still sees it), positioned directly
         * below Projects, above Reports. */}
        {canHandOutWork ? (
          <button type="button" class="admin-row" onClick={() => navigate("/contacts")}>
            <span class="admin-row-main">
              <span class="admin-row-name">Contacts</span>
              <span class="admin-row-sub">Who to call for what.</span>
            </span>
            <span class="chevron" aria-hidden="true">
              ›
            </span>
          </button>
        ) : null}

        {/* Sprint-009 B4 (design-phase-b.md §1.2) — same gate/position rule
         * as Projects above it: standalone, not admin-console-only (a
         * manager without `isAdmin` still sees it). */}
        {canHandOutWork ? (
          <button type="button" class="admin-row" onClick={() => navigate("/reports")}>
            <span class="admin-row-main">
              <span class="admin-row-name">Reports</span>
              <span class="admin-row-sub">How the farm's been doing.</span>
            </span>
            <span class="chevron" aria-hidden="true">
              ›
            </span>
          </button>
        ) : null}

        {/* Sprint-016 F3 (MORE-01) — real route, same `navigate()` shape
         * every sibling row above already uses (was local `subView` state
         * — see this file's module docstring). */}
        <button type="button" class="admin-row" onClick={() => navigate("/settings")}>
          <span class="admin-row-main">
            <span class="admin-row-name">{SIGN_IN_SETTINGS_ROW_TITLE}</span>
            <span class="admin-row-sub">{SIGN_IN_SETTINGS_ROW_SUB}</span>
          </span>
          <span class="chevron" aria-hidden="true">
            ›
          </span>
        </button>
      </div>

      <ReminderToggleRow reminderPrefs={reminderPrefs} />

      {onSignOut ? (
        <div class="sign-out-section">
          <SignOutButton onSignOut={onSignOut} />
        </div>
      ) : null}
    </section>
  );
}
